import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { SERVICES } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  service: SERVICES[0],
  event_date: "",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSubmitted({ name: form.name, service: form.service, email: form.email });
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-white/30 focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-40 bg-ink">
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24">
          <div>
            <Reveal><Overline>Let's Create Together</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
                Book Teresa for <span className="text-gold-gradient italic">speaking, ministry, or directing</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-white/60 leading-relaxed max-w-md">
                Whether it's a conference keynote, a worship experience, a theatrical
                production, or mentorship — share your vision and let's bring it to life.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 space-y-3 text-sm">
                <p className="text-white/40 uppercase tracking-[0.25em] text-xs">Inquiries</p>
                <a href="mailto:booking@mschancellor.com" className="block text-lg text-gold hover:text-gold-light transition-colors">
                  booking@mschancellor.com
                </a>
                <p className="font-serif italic text-white/70 text-xl pt-4">
                  "If it's in your mind, it's in your future."
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="confirmation"
                  data-testid="booking-confirmation"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="relative border border-gold/25 rounded-sm p-10 sm:p-12 bg-gradient-to-b from-white/[0.03] to-transparent"
                >
                  <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(212,175,55,0.12), transparent 70%)" }} />
                  <motion.div
                    initial={{ scale: 0, rotate: -30 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
                    className="relative w-16 h-16 rounded-full border border-gold/50 flex items-center justify-center text-gold"
                  >
                    <Check size={28} strokeWidth={1.5} />
                  </motion.div>
                  <h3 className="relative mt-8 font-serif font-light text-4xl sm:text-5xl tracking-tight">
                    Thank you, <span className="text-gold-gradient italic">{submitted.name.split(" ")[0]}.</span>
                  </h3>
                  <p className="relative mt-5 text-white/65 leading-relaxed max-w-md">
                    Your request regarding <span className="text-white">{submitted.service}</span> has been received.
                    A confirmation has been sent to <span className="text-gold">{submitted.email}</span>, and Teresa's
                    team will be in touch personally.
                  </p>
                  <p className="relative mt-6 font-serif italic text-white/70 text-lg">
                    "If it's in your mind, it's in your future."
                  </p>
                  <button
                    data-testid="booking-reset"
                    onClick={() => setSubmitted(null)}
                    className="relative mt-9 inline-flex items-center px-8 py-3.5 border border-gold/60 text-gold text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold hover:text-black transition-all duration-500"
                  >
                    Send Another Request
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.5 }}
                  onSubmit={submit}
                  data-testid="booking-form"
                  className="space-y-7"
                >
                  <div className="grid sm:grid-cols-2 gap-7">
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Full Name *</label>
                      <input data-testid="booking-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Email *</label>
                      <input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Phone</label>
                      <input data-testid="booking-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(optional)" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Organization</label>
                      <input data-testid="booking-org" value={form.organization} onChange={set("organization")} className={inputCls} placeholder="(optional)" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Service</label>
                      <select data-testid="booking-service" value={form.service} onChange={set("service")} className={`${inputCls} [&>option]:bg-ink`}>
                        {SERVICES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Preferred Date</label>
                      <input data-testid="booking-date" value={form.event_date} onChange={set("event_date")} className={inputCls} placeholder="(optional)" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Message *</label>
                    <textarea data-testid="booking-message" value={form.message} onChange={set("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about your event or vision..." />
                  </div>
                  <button
                    type="submit"
                    data-testid="booking-submit"
                    disabled={loading}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-10 py-4 bg-gold text-black text-xs uppercase tracking-[0.25em] rounded-sm hover:bg-gold-light transition-colors duration-500 disabled:opacity-50"
                  >
                    {loading ? "Sending..." : "Send Request"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
